/**
 * Carousel Web Component
 * ----------------------
 * Defines a custom element <carousel-component> that displays a
 * horizontally scrollable collection of <card-component> elements.
 *
 * Features:
 * - Supports manual navigation with left/right arrows.
 * - Optional label navigation for each card (via card `label` attribute).
 * - Supports auto-scrolling animation (`autoScroll` attribute).
 * - Supports native horizontal scrolling when `allowScroll` is set.
 * - Shadow DOM encapsulated for style isolation.
 *
 * Attributes:
 * - autoScroll: Automatically scrolls the carousel infinitely.
 * - showNavbar: Displays navigation arrows and card label buttons.
 * - allowScroll: Enables native scrolling (disables mask/edge limits).
 *
 * Example Usage:
 *  <carousel-component showNavbar>
 *    <card-component label="Step 1">...</card-component>
 *    <card-component label="Step 2">...</card-component>
 *  </carousel-component>
 *
 */

const Carousel = (function () {
  // --- Define HTML template with structure and styles ---
  let carouselTemplate = document.createElement("template");
  carouselTemplate.innerHTML = `            
    <style>
      .carousel-group {
        display: flex;
        justify-content: center;
        align-items: center;
        position: relative;
        flex-direction: column;
      }
      
      .nav-bar {
        position: relative;
        width: max-content;
        display: flex;
        justify-content: center;
        gap: 1.5rem;
        margin-top: 3rem;
      }

      .label-container {
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 1rem;
        gap: 1.5rem;
      }

      .label-button {
        background: white;
        display: flex;
        justify-content: center;
        align-items: center;
        color: #000;
        border: none;
        font-size: 0.875rem;
        padding: 0.5rem 0.75rem;
        min-width: 1.5rem;
        min-height: 2.25rem;
        border-radius: 2rem;
        cursor: pointer;
        border: 1px solid #f5f5f5;
      }
      .label-button:hover, .carousel-arrow:hover{
        border-color: lightgrey;
      }
      .label-button-active, .label-button-active:hover {
        border-color: #000;
      }

      .carousel-arrow {
        background: white;
        display: flex;
        justify-content: center;
        align-items: center;
        color: #000;
        border: none;
        font-size: 1.25rem;
        cursor: pointer;
        width: 2.25rem;
        height: 2.25rem;
        border-radius: 2rem;
        padding-bottom: 0.25rem;
        border: 1px solid #f5f5f5;
      }
      
      .carousel-arrow-disabled {
        color: lightgrey;
        cursor: auto;
      }
      .carousel-arrow-disabled:hover {
        border-color: #f5f5f5;
      }

      .carousel-arrow-prev { left: 20rem; }
      .carousel-arrow-next { right: 20rem; }

      .carousel-container {
        width: 100%;
        overflow-x: auto;
        display: flex;
        gap: 2rem;
        align-items: center;
        flex-flow: row nowrap;
        scroll-behavior: smooth;
        scrollbar-width: none;
        -ms-overflow-style: none;
      }

      .carousel-container::-webkit-scrollbar {
        display: none;
      }

      .carousel-mask {
        overflow: hidden;
        pointer-events: none;
        touch-action: none;
      }

      .mask-edges {
        mask-image: linear-gradient(to right, transparent, black 5%, black 95%, transparent);
        -webkit-mask-image: linear-gradient(to right, transparent, black 5%, black 95%, transparent);
      }

      .hidden { display: none; }

      .carousel-wrapper {
        display: flex;
        gap: 2rem;
      }
      .right-card-padding {
        padding-right: 32rem;
      }
      .carousel-autoscroll {
        width: max-content;
        animation: scroll 40s linear infinite;
        will-change: transform;
        padding-right: 2rem;
      }

      .carousel-play-pause {
        position: absolute;
        bottom: 1.4rem;
        right: -1rem;
        background: white;
        border: none;
        border-radius: 2rem;
        width: 2rem;
        height: 2rem;
        font-size: 0.875rem;
        cursor: pointer;
        color: lightgrey;
        border: 1px solid #f5f5f5;
      }
      .carousel-play-pause:hover, .carousel-play-pause:focus-visible {
        color: grey;
        border-color: lightgrey;
      }
      ::slotted(*) {
        flex: 0 0 auto;
        margin-right: 1rem;
      }

      @keyframes scroll {
        0% { transform: translateX(0); }
        100% { transform: translateX(-50%); }
      }
          
      @media (max-width: 760px) {
        .carousel-container{
          justify-content: center;
        }
        :host([showNavbar="true"]) .carousel-wrapper {
          flex-direction: column;
          gap: 3.75rem;
          align-items: center;
          width: 100%;
        }
        .right-card-padding {
          padding-right: 0;
        }
        .nav-bar {
          display: none;
        }
      }
    </style>

    <div class="carousel-group">
      <div id="carousel-container" class="carousel-container carousel-mask">
        <div id="carousel-wrapper" class="carousel-wrapper">
          <slot id="carousel-slot1"></slot>
          <slot id="carousel-slot2" name="carousel-slot2"></slot>
        </div>
      </div>

      <div class="nav-bar hidden" id="nav-bar">
        <button id="carousel-left-arrow" class="carousel-arrow carousel-arrow-disabled carousel-arrow-prev" aria-disabled="true" aria-label="Previous slide" aria-controls="carousel-container">&#8249;</button>
        <div id="label-container" class="label-container"></div>
        <button id="carousel-right-arrow" class="carousel-arrow carousel-arrow-next" aria-label="Next slide" aria-controls="carousel-container">&#8250;</button>    
      </div>
      <button id="carousel-play-pause" class="carousel-play-pause hidden" aria-label="Pause auto scroll">❚❚</button>
    </div>
  `;

  /**
   * Carousel custom element definition.
   */
  class Carousel extends HTMLElement {
    constructor() {
      super();
    }

    /**
     * Lifecycle: Called when the element is inserted into the DOM.
     * Sets up shadow DOM, references to internal elements, and handles
     * behavior for attributes like autoScroll, showNavbar, and allowScroll.
     */
    connectedCallback() {
      const template = carouselTemplate;
      this.shadowroot = this.attachShadow({ mode: "open" });
      this.shadowroot.appendChild(template.content.cloneNode(true));

      // --- Element references ---
      this.carouselContainer =
        this.shadowroot.getElementById("carousel-container");
      this.carouselWrapper = this.shadowroot.getElementById("carousel-wrapper");
      this.navBar = this.shadowroot.getElementById("nav-bar");
      this.leftArrow = this.shadowroot.getElementById("carousel-left-arrow");
      this.rightArrow = this.shadowroot.getElementById("carousel-right-arrow");
      this.labelContainer = this.shadowroot.getElementById("label-container");
      this.slot1 = this.shadowroot.getElementById("carousel-slot1");
      this.slot2 = this.shadowroot.getElementById("carousel-slot2");
      this.playPauseBtn = this.shadowroot.getElementById("carousel-play-pause");

      // --- Initialize cards and navigation labels ---
      this.cards = this.querySelectorAll("card-component");
      this.cardCount = this.cards.length;
      this.currentCardIndex = 0;

      // --- Handle attributes controlling carousel behavior ---
      this.handleVariants();

      // --- Observe for dynamic additions of cards ---
      this.observeCarousel();
    }

    /**
     * Handles carousel behavior based on attributes:
     * - autoScroll: Enables continuous auto-scrolling animation.
     * - showNavbar: Displays navigation arrows and label buttons.
     * - default: Disables edge masking and allows native scrolling.
     */
    handleVariants() {
      if (this.getAttribute("autoScroll")) {
        // Enable auto-scrolling mode
        this.carouselWrapper.classList.add("carousel-autoscroll");
        this.carouselContainer.classList.add("mask-edges");
        // --- Call loop() after 100ms to allow initial rendering ---
        setTimeout(() => this.loop(), 100);
        // --- Play/Pause Button for autoScroll ---
        this.playPauseBtn.classList.remove("hidden");
        this.isPaused = false;
        this.playPauseBtn.addEventListener("click", () => {
          this.playPause();
        });
      } else if (this.getAttribute("showNavbar")) {
        // Enable navigation bar with arrows and labels
        this.navBar.classList.remove("hidden");
        this.carouselWrapper.classList.add("right-card-padding");
        this.leftArrow.addEventListener("click", () =>
          this.moveToCard(this.currentCardIndex - 1),
        );
        this.rightArrow.addEventListener("click", () =>
          this.moveToCard(this.currentCardIndex + 1),
        );
      } else {
        // Default view (no mask, no navbar)
        this.carouselContainer.classList.remove("carousel-mask");
      }
    }

    /**
     * Sets up a MutationObserver to monitor additions of child <card-component>
     * elements. Updates navigation labels accordingly.
     */
    observeCarousel() {
      const observer = new MutationObserver(() => {
        if (this.getAttribute("autoScroll")) {
          return;
        }

        this.cards = this.querySelectorAll("card-component");
        if (this.cardCount === this.cards.length) return;

        this.cardCount = this.cards.length;

        // Create label button for each new card
        const i = this.cardCount - 1;
        const card = this.cards[i];

        const btn = document.createElement("button");
        btn.className =
          "label-button" + (i === 0 ? " label-button-active" : "");
        btn.textContent = card.getAttribute("label") || `${this.cardCount}`;
        btn.addEventListener("click", () => this.moveToCard(i));
        if (!card.getAttribute("label") && !this.getAttribute("showButtons"))
          btn.className += " hidden";
        this.labelContainer.appendChild(btn);
      });
      observer.observe(this, { childList: true });
    }

    /**
     * Scrolls carousel to a specific card index.
     * Updates arrow states based on position.
     */
    moveToCard(index) {
      if (index < 0 || index >= this.cards.length) return;

      // Scroll to target card
      const targetCard = this.cards[index];
      this.carouselContainer.scrollTo({
        left: targetCard.offsetLeft - this.cards[0].offsetLeft,
        behavior: "smooth",
      });

      // Update active label button
      this.labelContainer.childNodes[this.currentCardIndex].classList.remove(
        "label-button-active",
      );
      this.labelContainer.childNodes[index].classList.add(
        "label-button-active",
      );

      this.currentCardIndex = index;

      // Update arrow enable/disable states
      if (this.currentCardIndex === 0) {
        this.leftArrow.classList.add("carousel-arrow-disabled");
      } else {
        this.leftArrow.classList.remove("carousel-arrow-disabled");
      }

      if (this.currentCardIndex === this.cards.length - 1) {
        this.rightArrow.classList.add("carousel-arrow-disabled");
      } else {
        this.rightArrow.classList.remove("carousel-arrow-disabled");
      }
      this.rightArrow.setAttribute(
        "aria-disabled",
        this.currentCardIndex === this.cards.length - 1,
      );
      this.leftArrow.setAttribute("aria-disabled", this.currentCardIndex === 0);
    }

    /**
     * Continuous looping carousel behavior.
     * - Clone cards to simulate infinite loop.
     * - Reposition cards dynamically when reaching ends.
     */
    loop() {
      this.playPause(false); // Pause animation before cloning cards
      const assigned = this.slot1.assignedElements({ flatten: true });
      assigned.forEach((el) => {
        const clone = el.cloneNode(true);
        clone.setAttribute("slot", "carousel-slot2");
        this.appendChild(clone);
      });
      requestAnimationFrame(() => this.playPause(true)); // Play on next frame
    }

    /**
     * Controls the auto-scroll animation state.
     * - Toggles play/pause when called without arguments.
     * - Accepts an optional boolean to force a specific state:
     *    - true → force play
     *    - false → force pause
     */
    playPause(forceState = null) {
      if (forceState === true) {
        this.isPaused = false;
      } else if (forceState === false) {
        this.isPaused = true;
      } else {
        this.isPaused = !this.isPaused;
      }
      this.carouselWrapper.style.animationPlayState = this.isPaused
        ? "paused"
        : "running";
      this.playPauseBtn.innerHTML = this.isPaused ? "&nbsp▶" : "❚❚";
      this.playPauseBtn.setAttribute(
        "aria-label",
        this.isPaused ? "Play auto scroll" : "Pause auto scroll",
      );
    }
  }

  // --- Register the custom element ---
  customElements.define("carousel-component", Carousel);

  return Carousel;
})();

export { Carousel };
